import { type FormikHelpers } from 'formik'
import { useEffect, useState } from 'react'

import { useDialog } from '@loadsmart/loadsmart-ui'
import { Button, Divider, Field, Icon, Layout, Link } from '@loadsmart/miranda-react'

import type {
  OpenDockWarehouseCustomFormField,
  OpenDockWarehouseFormField,
} from '../../infra/ShipmentMapper'
import UploadDocumentsDialog from './UploadDocumentsDialog/UploadDocumentsDialog'

const DOC_FILE_COUNT_LIMIT = 1
const MULTI_DOC_FILE_COUNT_LIMIT = 10

type FilesUploaded = {
  name: string
  storageUrl: string
  storageKey: string
}[]

function uploadedFilesInitializer(value: FilesUploaded): FilesUploaded {
  if (Array.isArray(value)) {
    return value
  }
  return []
}

export function DocAndMultiDocField({
  field,
  shipmentUUID,
  setFieldValue,
  fieldName,
  value,
}: {
  field: OpenDockWarehouseCustomFormField | OpenDockWarehouseFormField
  shipmentUUID: string
  setFieldValue: FormikHelpers<unknown>['setFieldValue']
  fieldName: string
  value: FilesUploaded
}) {
  const { open, show, hide } = useDialog({ open: false })
  const [uploadedFiles, setUploadedFiles] = useState<FilesUploaded>(() =>
    uploadedFilesInitializer(value)
  )

  useEffect(() => {
    setFieldValue(fieldName, uploadedFiles)
  }, [uploadedFiles, setFieldValue, fieldName])

  return (
    <Layout.Stack gap="spacing-2">
      <Field>
        <Field.Label required={field.required}>{field.label}</Field.Label>
        <Button
          size="small"
          leading={<Icon name="upload" color="color-primary-100" />}
          variant="secondary"
          style={{ width: 'fit-content' }}
          onClick={show}
        >
          Upload
        </Button>
      </Field>
      {uploadedFiles.map(uploadedFile => (
        <>
          <Layout.Group key={uploadedFile.storageKey} align="center" justify="space-between">
            <Link
              href={`${process.env.REACT_APP_OPENDOCK_FILE_URL}${uploadedFile.storageUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {uploadedFile.name}
            </Link>
            <Button
              variant="icon"
              size="small"
              onClick={() => {
                setUploadedFiles(prevState =>
                  prevState.filter(file => file.storageKey !== uploadedFile.storageKey)
                )
              }}
            >
              <Icon name="trash" color="color-primary-100" />
            </Button>
          </Layout.Group>
          <Divider />
        </>
      ))}
      <UploadDocumentsDialog
        open={open}
        onClose={hide}
        attachedFilesLength={uploadedFiles.length}
        allowedFileCount={field.type === 'doc' ? DOC_FILE_COUNT_LIMIT : MULTI_DOC_FILE_COUNT_LIMIT}
        onFilesUploaded={setUploadedFiles}
        shipmentUUID={shipmentUUID}
      />
    </Layout.Stack>
  )
}
